import type { Session } from '@supabase/supabase-js';
import { supabase } from './supabaseClient';

type SessionListener = (session: Session | null) => void;

/**
 * Thin wrapper around Supabase's own auth state, mirroring AppStore's
 * singleton/subscribe shape rather than scattering supabase.auth.* calls
 * across every UI section that needs to know whether someone's signed in.
 *
 * Every method here is a safe no-op when Supabase isn't configured
 * (supabase is null) — callers never need their own supabaseConfigured
 * check before using this store.
 */
class AuthStore {
  private session: Session | null = null;
  private listeners = new Set<SessionListener>();
  private signedInListeners = new Set<() => void>();
  private initPromise: Promise<void> | null = null;

  /** Idempotent — safe to call more than once (e.g. hot reload); only the
   * first call actually does anything. */
  init(): Promise<void> {
    if (!this.initPromise) this.initPromise = this.doInit();
    return this.initPromise;
  }

  private async doInit(): Promise<void> {
    if (!supabase) return;
    const { data } = await supabase.auth.getSession();
    this.session = data.session;
    supabase.auth.onAuthStateChange((event, session) => {
      this.session = session;
      this.notify();
      // 'SIGNED_IN' only fires for a genuine new sign-in (a magic-link or
      // OAuth redirect completing, or an explicit call) — a session
      // restored from a previous visit fires 'INITIAL_SESSION' instead, so
      // onSignedIn listeners never fire just from opening the app with an
      // already-valid session.
      if (event === 'SIGNED_IN') {
        for (const listener of this.signedInListeners) listener();
      }
    });
  }

  getSession(): Session | null {
    return this.session;
  }

  isSignedIn(): boolean {
    return this.session !== null;
  }

  subscribe(listener: SessionListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /** Fires only on a genuine new sign-in — see the comment in doInit. */
  onSignedIn(listener: () => void): () => void {
    this.signedInListeners.add(listener);
    return () => this.signedInListeners.delete(listener);
  }

  private notify(): void {
    for (const listener of this.listeners) listener(this.session);
  }

  /** Returns an error message, or null on success. Success here means "the
   * email was sent" — it does not mean the user is signed in yet; that
   * only happens once they click the link and the redirect completes. */
  async sendMagicLink(email: string): Promise<string | null> {
    if (!supabase) return 'Supabase is not configured in this build.';
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: window.location.origin },
    });
    return error?.message ?? null;
  }

  async signOut(): Promise<void> {
    if (!supabase) return;
    await supabase.auth.signOut();
  }
}

export const authStore = new AuthStore();
